import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import {
    Avatar,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Menu,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { useDataStore } from "app/store/dataStore";
import { useSocketStore } from "app/store/socketStore";
import { getUrlParams } from "app/store/dataUtils";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { IChat as IChatRoom, WarehouseType } from "src/app/types";
import _ from "lodash";
//import Vectorsvg from "src/assets/icons/Vector.svg";
const StyledMenuItem = withStyles((theme) => ({
    root: {
        "&:focus": {
            "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                color: theme.palette.common.white,
            },
        },
    },
}))(MenuItem);

const Loader = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        fill="#000000"
        viewBox="0 0 256 256"
        className="animate-spin"
    >
        <path d="M232,128a104,104,0,0,1-208,0c0-41,23.81-78.36,60.66-95.27a8,8,0,0,1,6.68,14.54C60.15,61.59,40,93.27,40,128a88,88,0,0,0,176,0c0-34.73-20.15-66.41-51.34-80.73a8,8,0,0,1,6.68-14.54C208.19,49.64,232,87,232,128Z"></path>
    </svg>
);

export type TabType = "drivers" | "shipments";
export type LoadingType = "none" | "warehouses" | "drivers" | "shipments";

const TABS: Array<{ id: TabType; label: string }> = [
    {
        id: "drivers",
        label: "Drivers",
    },
    {
        id: "shipments",
        label: "Shipments",
    },
];

type DriverChatRoom = Partial<IChatRoom> & { hasChat?: boolean; unread_messages?: number };

export function ChatLeftSidebar() {
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [warehouseSearchQuery, setWarehouseSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState<LoadingType>("none");
    const [selectedTabId, setSelectedTabId] = useState<TabType>("drivers");

    // Scope declared by the launch URL's NEW-contract `warehouses` param:
    // only these warehouses ever render, and a passed driver then locks the
    // operator list to that one driver. Legacy launches (singular `warehouse`
    // context param, or none) declare no scope and behave exactly as before
    // the restructure — full warehouse list, all operators.
    const { warehouses: scopedWarehouses, driver } = useMemo(() => getUrlParams(), []);
    const scopedDriver = scopedWarehouses?.length ? driver : undefined;

    const [warehouses, setWarehouses] = useState([]);
    const [driverChatRooms, setDriverChatRooms] = useState<DriverChatRoom[]>([]);
    const [allDrivers, setAllDrivers] = useState<Array<{ _id: string; name?: string }>>([]);
    const [shipmentChatRooms, setShipmentChatRooms] = useState<Partial<IChatRoom>[]>([]);

    const { messageSocket, isConnected } = useSocketStore();

    const isValidChatId = (value?: string) => typeof value === "string" && /^[a-f0-9]{24}$/i.test(value);
    const normalizeChatId = (value: unknown): string | undefined => {
        if (!value) return undefined;
        if (typeof value === "string") return value;
        if (typeof value === "object") {
            const maybeOid = (value as any)?.$oid;
            if (typeof maybeOid === "string") return maybeOid;
            if (typeof (value as any)?.toHexString === "function") return (value as any).toHexString();
        }
        try {
            return String(value);
        } catch {
            return undefined;
        }
    };

    const mapDriverRooms = (data: any[]): DriverChatRoom[] => {
        if (!Array.isArray(data)) return [];
        return data.map((room) => {
            const normalizedId = normalizeChatId(room?._id) || room?._id;
            const unreadCount = typeof room?.unread_messages === "number" ? room.unread_messages : 0;
            console.log("[mapDriverRooms]", room?.driver, "unread:", unreadCount);
            return {
                ...(room || {}),
                _id: normalizedId,
                hasChat: true,
                unread_messages: unreadCount,
            } as DriverChatRoom;
        });
    };
    const { currentWarehouse, currentChatRoom, setCurrentChatRoom, setCurrentWarehouse } = useDataStore();

    useEffect(() => {
        if (!isConnected) return;

        handleFetchWarehouses();

        return () => {
            messageSocket.off("warehouses");
        };
    }, [isConnected]);

    useEffect(() => {
        if (!messageSocket) return;

        const handleDriverChatRoomsUpdate = (data: any) => {
            console.log("[driver-chat-rooms] received update", data);
            setDriverChatRooms(mapDriverRooms(data));
        };

        messageSocket.on("driver-chat-rooms", handleDriverChatRoomsUpdate);

        return () => {
            messageSocket.off("driver-chat-rooms", handleDriverChatRoomsUpdate);
        };
    }, [messageSocket]);

    useEffect(() => {
        if (!messageSocket) return;
        if (selectedTabId === "drivers") {
            if (!currentWarehouse) return;
            setIsLoading("drivers");
            messageSocket.emit("warehouse-drivers::fetch", {
                warehouseId: currentWarehouse._id,
            });
            messageSocket.on("warehouse-drivers", (data) => {
                setIsLoading("none");
                setAllDrivers(Array.isArray(data) ? data : []);
            });
            messageSocket.emit("driver-chat-rooms::fetch", { warehouseId: currentWarehouse._id });
        } else if (selectedTabId === "shipments") {
            if (currentWarehouse) handleFetchShipmentChatRooms();
        }

        return () => {
            messageSocket.off("warehouse-drivers");
            messageSocket.off("shipments-chat-rooms");
        };
    }, [selectedTabId, currentWarehouse, messageSocket, searchQuery]);

    const driversUnion = useMemo(() => {
        const map = new Map<string, any>();
        allDrivers.forEach((d) => {
            if (!d?._id) return;
            map.set(d._id, { _id: d._id, name: d.name, hasChat: false, unread_messages: 0 });
        });
        (driverChatRooms || []).forEach((cr) => {
            const id = cr?.driver as string;
            if (!id) return;
            const existing = map.get(id) || { _id: id };
            map.set(id, {
                ...existing,
                name: existing.name || cr?.driver_name,
                hasChat: true,
                chatId: cr?._id,
                last_message_text: cr?.last_message_text,
                last_message_datetime: cr?.last_message_datetime,
                unread_messages: cr?.unread_messages || 0,
            });
        });
        return Array.from(map.values()).sort((a, b) => {
            if (a.hasChat && !b.hasChat) return -1;
            if (!a.hasChat && b.hasChat) return 1;

            if (a.hasChat && b.hasChat) {
                const dateA = a.last_message_datetime ? new Date(a.last_message_datetime).getTime() : 0;
                const dateB = b.last_message_datetime ? new Date(b.last_message_datetime).getTime() : 0;
                return dateB - dateA;
            }

            return (a.name || a._id).localeCompare(b.name || b._id);
        });
    }, [allDrivers, driverChatRooms]);

    const filteredDriversUnion = useMemo(() => {
        let drivers = driversUnion;
        if (scopedDriver) {
            drivers = drivers.filter((d) => (d._id || "").toUpperCase() === scopedDriver);
        }
        if (!searchQuery) return drivers;
        const q = searchQuery.toLowerCase();
        return drivers.filter(
            (d) => (d.name || "").toLowerCase().includes(q) || (d._id || "").toLowerCase().includes(q)
        );
    }, [searchQuery, driversUnion, scopedDriver]);

    const filteredShipmentChatRooms = useMemo(() => {
        if (!scopedDriver) return shipmentChatRooms;
        return shipmentChatRooms.filter((room) => ((room?.driver as string) || "").toUpperCase() === scopedDriver);
    }, [shipmentChatRooms, scopedDriver]);

    function handleFetchWarehouses() {
        setIsLoading("warehouses");
        messageSocket.emit("warehouses::fetch");
        messageSocket.on("warehouses", (data) => {
            setIsLoading("none");
            // The server already limits the list to the session's scope; the
            // client filter repeats it so an unscoped (legacy) backend still
            // never renders a warehouse the URL didn't name.
            let list = Array.isArray(data) ? data : [];
            if (scopedWarehouses?.length) {
                list = list.filter((w: any) => scopedWarehouses.includes(String(w._id).toUpperCase()));
            }
            setWarehouses(list);
            try {
                const store = useDataStore.getState();
                if (!store.currentWarehouse && list.length > 0) {
                    setCurrentWarehouse(list[0]);
                }
            } catch {}
        });
    }

    function handleFetchDriverChatRooms() {
        console.log("Fetching driver chat rooms for warehouse:", currentWarehouse?._id);
        setIsLoading("drivers");
        messageSocket.emit("driver-chat-rooms::fetch", { warehouseId: currentWarehouse._id });
        messageSocket.on("driver-chat-rooms", (data) => {
            console.log("Received driver chat rooms data:", data);
            setIsLoading("none");
            setDriverChatRooms(Array.isArray(data) ? data : []);
        });
    }

    function handleFetchShipmentChatRooms() {
        setIsLoading("shipments");
        messageSocket.emit("shipment-chat-rooms::fetch", { warehouseId: currentWarehouse._id, searchQuery });
        messageSocket.on("shipment-chat-rooms", (data) => {
            setIsLoading("none");
            setShipmentChatRooms(Array.isArray(data) ? data : []);
        });
    }

    function handleCloseMenu() {
        setAnchorEl(null);
        setWarehouseSearchQuery("");
    }

    function handleSelectWarehouse(wh: WarehouseType) {
        setCurrentWarehouse(wh);
    }

    function handleSelectChatRoom(chatRoom: Partial<IChatRoom>) {
        setCurrentChatRoom(chatRoom);

        if (messageSocket && chatRoom._id) {
            messageSocket.emit("chat-room::join", { chatId: chatRoom._id });
        }
    }

    async function handleStartChatWithDriver(driver: {
        _id: string;
        name?: string;
        hasChat?: boolean;
        chatId?: string;
    }) {
        if (!currentWarehouse) return;
        try {
            const existing = driverChatRooms?.find((cr) => cr.driver === driver._id);
            if (existing) {
                handleSelectChatRoom(existing);
                return;
            }
            const newRoom: Partial<IChatRoom> = {
                type: "WAREHOUSE",
                warehouse: currentWarehouse._id,
                driver: driver._id,
                driver_name: driver.name,
            } as any;
            handleSelectChatRoom(newRoom);
        } catch (e) {}
    }

    function getChatHeaderAvatar() {
        const title = currentChatRoom?.driver;
        if (!title) return "";
        const parts = title.trim().split(/\s+/);
        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        } else {
            return parts.map((part) => part[0].toUpperCase()).join("");
        }
    }

    return (
        <motion.div
            exit={{ x: -320 }}
            animate={{ x: 0 }}
            initial={{ x: -320 }}
            transition={{ duration: 0.3 }}
            className="max-w-[750px] w-full  h-full p-0 sm:w-[450px] md:w-[650px] lg:w-[750px] font-[Inter]"
        >
            <Card className="border-r  rounded-none  bg-white">
                <CardHeader
                    className="border-b"
                    sx={{ padding: "" }}
                    title={
                        <div className="flex justify-between items-start ">
                            <div className="flex gap-6 items-center">
                                <div
                                    className="flex items-center justify-center bg-[color:var(--rc-accent-faint,#EBF2FE)] border border-[color:var(--rc-accent,#0741AC)] gap-6 "
                                    style={{
                                        width: "43px",
                                        height: "42px",
                                        opacity: 1,
                                        padding: "2px",
                                        gap: "14px",
                                        borderRadius: "3px",
                                        borderWidth: "1px",
                                    }}
                                >
                                    <img src="/assets/icons/Vector.svg" />
                                </div>
                                <div>
                                    <Typography
                                        sx={{ fontSize: "16px", fontWeight: 600, color: "var(--rc-ink, #181819)", lineHeight: 1.4 }}
                                    >
                                        {currentWarehouse ? currentWarehouse.name : "Select Warehouse"}
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: "13px", color: "var(--rc-ink-soft, #656669)", marginTop: "2px", fontWeight: 600 }}
                                    >
                                        {selectedTabId === "drivers"
                                            ? filteredDriversUnion?.length || 0
                                            : shipmentChatRooms?.length || 0}{" "}
                                        Chats
                                    </Typography>
                                </div>
                            </div>
                            <IconButton
                                size="small"
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                sx={{ marginTop: "6px" }}
                            >
                                <FuseSvgIcon size={20}>heroicons-outline:chevron-down</FuseSvgIcon>
                            </IconButton>
                        </div>
                    }
                />
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    PaperProps={{
                        sx: {
                            width: 427,
                            borderRadius: "4px",
                            mt: 2,
                            padding: 0,
                            border: "1px solid #e5e7eb",
                            maxHeight: "calc(100vh - 100px)",
                            backgroundColor: "#FFF",
                            boxShadow: "4px 4px 24px -12px rgba(16, 25, 40, 0.12)",
                            overflow: "hidden",
                        },
                    }}
                    MenuListProps={{
                        sx: {
                            padding: 0,
                        },
                        autoFocusItem: false,
                        disableListWrap: true,
                    }}
                >
                    {isLoading === "warehouses" ? (
                        <div className="flex justify-center items-center p-12">
                            <Loader />
                        </div>
                    ) : (
                        <div className="p-[12px] flex flex-col gap-[12px]">
                            {/* Header with close button */}
                            <div className="flex items-center gap-1 w-full">
                                <img src="/assets/icons/warehouse.svg" className=" flex-shrink-0" />
                                <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#323232", flex: 1 }}>
                                    Warehouse
                                </Typography>
                                <IconButton onClick={handleCloseMenu} sx={{ padding: "6px" }}>
                                    <FuseSvgIcon size={20} color="action">
                                        heroicons-outline:x
                                    </FuseSvgIcon>
                                </IconButton>
                            </div>

                            {/* Search Bar */}
                            <div>
                                <TextField
                                    variant="outlined"
                                    placeholder="Search"
                                    fullWidth
                                    value={warehouseSearchQuery}
                                    onChange={(e) => setWarehouseSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    InputProps={{
                                        endAdornment: (
                                            <FuseSvgIcon size={16} color="action">
                                                heroicons-outline:search
                                            </FuseSvgIcon>
                                        ),
                                        sx: {
                                            height: "36px",
                                            borderRadius: "4px",
                                            fontSize: "13px",
                                            backgroundColor: "#fff",
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#DFE2E8",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#DFE2E8",
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#DFE2E8",
                                                borderWidth: "1px",
                                            },
                                        },
                                    }}
                                    size="small"
                                />
                            </div>

                            {/* Warehouse List */}
                            <div className="flex flex-col gap-[8px] overflow-auto max-h-[250px]">
                                {warehouses
                                    ?.filter(
                                        (w) =>
                                            w.name.toLowerCase().includes(warehouseSearchQuery.toLowerCase()) ||
                                            w._id.toLowerCase().includes(warehouseSearchQuery.toLowerCase())
                                    )
                                    .map((w, index) => (
                                        <>
                                            {index > 0 && (
                                                <div key={`divider-${w._id}`} className="h-px bg-[#F5F5F5] w-full" />
                                            )}
                                            <div
                                                key={w._id}
                                                onClick={() => {
                                                    handleSelectWarehouse(w);
                                                    handleCloseMenu();
                                                    setWarehouseSearchQuery("");
                                                }}
                                                className={`px-4 py-1 cursor-pointer rounded-[var(--rc-radius,4px)] transition-colors ${
                                                    currentWarehouse?._id === w._id
                                                        ? "bg-[color:var(--rc-accent-faint,#EBF2FE)]"
                                                        : "bg-white hover:bg-[color:var(--rc-hover,#F5F5F5)]"
                                                }`}
                                            >
                                                <div className="flex items-start py-4 justify-between gap-1">
                                                    <div className="flex-1 flex flex-col gap-1">
                                                        <Typography
                                                            sx={{
                                                                fontSize: "13px",
                                                                fontWeight: 600,
                                                                color: "#39393b",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {w.name} ({w._id})
                                                        </Typography>
                                                        <div className="flex items-center gap-[2px]">
                                                            <img src="/assets/icons/location.svg" className="flex-shrink-0" />
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "11px",
                                                                    fontWeight: 400,
                                                                    color: "#656669",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {w.address || "Address not available"}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                    {currentWarehouse?._id === w._id && (
                                                        <div className="flex-shrink-0">
                                                            <img src="/assets/icons/checker.svg" className=" flex-shrink-0" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ))}
                            </div>
                        </div>
                    )}
                </Menu>
                <CardContent className="h-[calc(100vh-80px)] flex border-b-0 flex-col p-0 gap-3">
                    {/* Tabs with Pill Style */}
                    <div className="px-14 py-4 mt-6  flex gap-10 ">
                        <button
                            onClick={() => {
                                const currentRoom = useDataStore.getState().currentChatRoom;
                                if (messageSocket && currentRoom?._id) {
                                    messageSocket.emit("chat-room::leave", { chatId: currentRoom._id });
                                }
                                setCurrentChatRoom(null);
                                setSelectedTabId("drivers");
                            }}
                            className={`px-4 py-2 text-sm font-semibold rounded-[var(--rc-radius,4px)] border transition-all w-72 h-32 ${
                                selectedTabId === "drivers"
                                    ? "border-[color:var(--rc-accent,#0741AC)] bg-[color:var(--rc-accent-faint,#eff6ff)] text-[color:var(--rc-accent,#0741AC)]"
                                    : "border-gray-300 bg-[color:var(--rc-hover,#F5F5F5)] text-gray-600 hover:bg-gray-400"
                            }`}
                        >
                            Operators
                        </button>
                        <button
                            onClick={() => {
                                const currentRoom = useDataStore.getState().currentChatRoom;
                                if (messageSocket && currentRoom?._id) {
                                    messageSocket.emit("chat-room::leave", { chatId: currentRoom._id });
                                }
                                setCurrentChatRoom(null);
                                setSelectedTabId("shipments");
                            }}
                            className={`px-4 py-2 text-sm font-semibold rounded-[var(--rc-radius,4px)] border transition-all w-92 h-32 ${
                                selectedTabId === "shipments"
                                    ? "border-[color:var(--rc-accent,#0741AC)] bg-[color:var(--rc-accent-faint,#eff6ff)] text-[color:var(--rc-accent,#0741AC)]"
                                    : "border-gray-300 bg-[color:var(--rc-hover,#F5F5F5)] text-gray-600 hover:bg-gray-400"
                            }`}
                        >
                            Shipments
                        </button>
                    </div>

                    {/* Chat Room List */}
                    <div className="h-full overflow-auto">
                        {selectedTabId === "drivers" && (
                            <div className="h-full">
                                {/* Search Bar and Toggle */}
                                <div className="px-14 py-3 flex justify-between items-center gap-4">
                                    <TextField
                                        variant="outlined"
                                        placeholder="Search"
                                        className="flex-1"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                <FuseSvgIcon size={18} color="action">
                                                    heroicons-outline:search
                                                </FuseSvgIcon>
                                            ),
                                            sx: {
                                                height: "40px",
                                                marginBottom: "6px",
                                                borderRadius: "5px",
                                                fontSize: "14px",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#E5E7EB",
                                                },
                                            },
                                        }}
                                        size="small"
                                    />
                                </div>
                                {isLoading === "drivers" ? (
                                    <div className="flex justify-center items-center h-full p-4">
                                        <Loader />
                                    </div>
                                ) : filteredDriversUnion?.length < 1 ? (
                                    <div className="flex justify-center items-center h-full p-4 text-[#605E5C]">
                                        No drivers found
                                    </div>
                                ) : (
                                    <List sx={{ padding: 0 }}>
                                        {filteredDriversUnion?.map((driver) => {
                                            const isSelected =
                                                driver._id === useDataStore.getState().currentChatRoom?.driver;
                                            const hasChat = !!driver.hasChat;
                                            const subtitle = hasChat
                                                ? driver.last_message_text || "View conversation"
                                                : "Tap to start chat";
                                            const unreadCount = driver.unread_messages || 0;

                                            return (
                                                <ListItem
                                                    key={driver._id}
                                                    onClick={() => handleStartChatWithDriver(driver)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        padding: "12px 16px ",
                                                        borderBottom: "1px solid var(--rc-line, #f3f4f6)",
                                                        backgroundColor: isSelected ? "var(--rc-selected, #f9fafb)" : "transparent",
                                                        "&:hover": {
                                                            backgroundColor: "var(--rc-hover, #EBF2FE)",
                                                        },
                                                    }}
                                                >
                                                    <ListItemAvatar sx={{ minWidth: "auto", marginRight: "16px" }}>
                                                        <Avatar
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: "var(--rc-chrome, #02163B)",
                                                                fontSize: "14px",
                                                                fontWeight: 600,
                                                                borderRadius: "var(--rc-radius, 8%)",
                                                            }}
                                                        >
                                                            {(driver.name || driver._id).slice(0, 2).toUpperCase()}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "14px",
                                                                    fontWeight: 500,
                                                                    color: "var(--rc-ink, #1a1a1a)",
                                                                    marginBottom: "2px",
                                                                }}
                                                            >
                                                                {driver.name || "Driver"} ({driver._id})
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "13px",
                                                                    color: "var(--rc-ink-soft, #6b7280)",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {subtitle}
                                                            </Typography>
                                                        }
                                                        sx={{ margin: 0 }}
                                                    />
                                                    {unreadCount > 0 && (
                                                        <div
                                                            style={{
                                                                backgroundColor: "var(--rc-accent, #3b82f6)",
                                                                color: "white",
                                                                borderRadius: "50%",
                                                                minWidth: "20px",
                                                                height: "20px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: "10px",
                                                                fontWeight: 600,
                                                                padding: "0 6px",
                                                                marginLeft: "8px",
                                                            }}
                                                        >
                                                            {unreadCount > 99 ? "99+" : unreadCount}
                                                        </div>
                                                    )}
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                )}
                            </div>
                        )}
                        {selectedTabId === "shipments" && (
                            <div className="h-full flex flex-col">
                                {/* Search Bar for Shipments */}
                                <div className="px-14  py-3 ">
                                    <TextField
                                        variant="outlined"
                                        placeholder="Search"
                                        fullWidth
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                <FuseSvgIcon size={18} color="action">
                                                    heroicons-outline:search
                                                </FuseSvgIcon>
                                            ),
                                            sx: {
                                                height: "40px",
                                                marginBottom: "6px",
                                                borderRadius: "4px",
                                                fontSize: "14px",
                                                backgroundColor: "#fff",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#e5e7eb",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "#d1d5db",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "var(--rc-accent, #3b82f6)",
                                                    borderWidth: "1px",
                                                },
                                            },
                                        }}
                                        size="small"
                                    />
                                </div>

                                {/* Shipments Table */}
                                <div className="flex-1 overflow-auto  px-16">
                                    {isLoading === "shipments" ? (
                                        <div className="flex justify-center items-center h-full p-4">
                                            <Loader />
                                        </div>
                                    ) : filteredShipmentChatRooms?.length < 1 ? (
                                        <div className="flex justify-center items-center h-full p-4 text-[#605E5C]">
                                            No shipments found
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto font-[Inter] rounded bg-white">
                                            <table className="min-w-full text-[13px]">
                                                <thead>
                                                    <tr className=" border-[#E5E7EB] bg-[color:var(--rc-hover,#F5F5F5)] text-[color:var(--rc-ink-soft,#656669)] mb-2">
                                                        <th className="px-6 py-10 text-left font-semibold text-[12px]">
                                                            Route Leg
                                                        </th>
                                                        <th className="px-6 py-10 text-left font-semibold text-[12px]">
                                                            Tracking Ref
                                                        </th>
                                                        <th className="px-6 py-10 text-left font-semibold text-[12px]">
                                                            Customer
                                                        </th>
                                                        <th className="px-6 py-10 text-left font-semibold text-[12px]">
                                                            Driver
                                                        </th>
                                                        <th className="px-6 py-10 text-left font-semibold text-[12px]"></th>
                                                    </tr>
                                                    <div className="h-2"/>
                                                </thead>
                                                <tbody>
                                                    {filteredShipmentChatRooms?.map((chatRoom) => {
                                                        console.log("Rendering chat room:", chatRoom);
                                                        const isSelected =
                                                            chatRoom._id ===
                                                            useDataStore.getState().currentChatRoom?._id;
                                                        return (
                                                            <tr
                                                                key={chatRoom?._id}
                                                                onClick={() => handleSelectChatRoom(chatRoom)}
                                                                className={`border-b border-[#F3F4F6] cursor-pointer transition-colors hover:bg-[color:var(--rc-hover,#EBF2FE)] ${isSelected ? "bg-[color:var(--rc-selected,#DFE2E8)]" : ""}`}
                                                            >
                                                                <td className="px-6 py-14 text-[#111827] ">
                                                                    {chatRoom?.shipment_route_leg_id || "-"}
                                                                </td>
                                                                <td className="px-6 py-14 text-[#374151]">
                                                                    {chatRoom?.shipment || "-"}
                                                                </td>
                                                                <td className="px-6 py-14 text-[#374151]">
                                                                    {chatRoom?.customer_supplier_id || "-"}
                                                                </td>
                                                                <td className="px-6 py-14 text-[#374151]">
                                                                    {chatRoom?.driver || "-"}
                                                                </td>
                                                                <td className="px-6 py-5">
                                                                    <IconButton size="small" sx={{ padding: "4px" }}>
                                                                        <FuseSvgIcon size={16} color="action">
                                                                            heroicons-outline:dots-vertical
                                                                        </FuseSvgIcon>
                                                                    </IconButton>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
